import { useState, type Ref } from "react";
import { assigneeTypeSchema, type AssigneeType } from "@io-spoke/contracts/assignments";
import type { OperationalObligation } from "@io-spoke/contracts/obligations";
import { obligationOwnership } from "../../lib/obligationOwnership";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export type OwnerSuggestion = { type: AssigneeType; name: string };

export function recordedOwnerSuggestions(obligations: OperationalObligation[]): OwnerSuggestion[] {
  const names = new Map<string, OwnerSuggestion>();
  for (const obligation of obligations) {
    if (!obligationOwnership(obligation).recorded || !obligation.ownerType || !obligation.ownerId?.trim()) continue;
    const type = assigneeTypeSchema.safeParse(obligation.ownerType);
    if (!type.success) continue;
    const name = obligation.ownerId.trim();
    names.set(`${type.data}:${name.toLocaleLowerCase()}`, { type: type.data, name });
  }
  return [...names.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function AccountableOwnerField({ id, type, value, onChange, onBlur, inputRef, disabled, error, suggestions }: {
  id: string; type: AssigneeType; value: string; onChange: (value: string) => void;
  onBlur: () => void; inputRef?: Ref<HTMLInputElement>; disabled: boolean; error?: string; suggestions: OwnerSuggestion[];
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const matches = suggestions.filter((item) => item.type === type && item.name.toLocaleLowerCase().includes(value.trim().toLocaleLowerCase()));
  const choose = (name: string) => { onChange(name); setSelected(`${type}:${name}`); };
  const showSuggestions = selected !== `${type}:${value}`;
  return <div className="space-y-2">
    <Label htmlFor={id}>Accountable owner</Label>
    <Input ref={inputRef} id={id} value={value} onChange={(event) => { setSelected(null); onChange(event.target.value); }} onBlur={onBlur} placeholder="Search recorded names or enter a new name" disabled={disabled} aria-invalid={Boolean(error)} aria-describedby={`${id}-help${error ? ` ${id}-error` : ""}`} autoComplete="off" />
    {showSuggestions ? <div className="rounded-md border p-2" role="group" aria-label="Recorded owner suggestions">
      <p className="px-2 py-1 text-xs text-muted-foreground">Names recorded on this workspace’s obligations</p>
      <div className="max-h-48 overflow-y-auto">{matches.map((item) => <Button key={item.name} type="button" variant="ghost" className="min-h-11 w-full justify-start whitespace-normal text-left" disabled={disabled} onClick={() => choose(item.name)}>{item.name}</Button>)}</div>
      {matches.length === 0 ? <p className="px-2 py-2 text-sm text-muted-foreground">No matching recorded names.</p> : null}
      {value.trim() && !matches.some((item) => item.name.toLocaleLowerCase() === value.trim().toLocaleLowerCase()) ? <Button type="button" variant="outline" className="min-h-11 w-full whitespace-normal" disabled={disabled} onClick={() => choose(value.trim())}>Use new name: {value.trim()}</Button> : null}
    </div> : null}
    <p id={`${id}-help`} className="text-xs leading-5 text-muted-foreground">Choose a recorded name or enter a new one. This records responsibility without changing the source, linking a workspace member, sending an invitation or notification, or confirming acceptance.</p>
    {error ? <p id={`${id}-error`} className="text-sm text-destructive" role="alert">{error}</p> : null}
  </div>;
}
